import { legal } from '../lib/market';
import {
  anafAlerts,
  anafBadge,
  countiesText,
  daysAgo,
  fmtNum,
  primaryContact,
  type Supplier,
  type SupplierScore,
  t,
} from '@procumi/shared';
import { Fragment, useMemo, useState } from 'react';
import { Avatar } from '../components/Avatar';
import { MoreMenu } from '../components/MoreMenu';
import { ErrorBox, Loading } from '../components/StateBox';
import { ImportSuppliersDialog } from '../components/ImportSuppliersDialog';
import { SupplierDialog } from '../components/SupplierDialog';
import { useAsync } from '../hooks/useAsync';
import type { Toast } from '../hooks/useToast';
import { api, errMsg } from '../lib/api';
import { ask } from '../lib/confirm';

/** Căutarea nu ține cont de diacritice și de litere mari. */
const plain = (t: string) =>
  t
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const ALL = t('Toți');

export function SuppliersPage({ toast, openImport }: { toast: Toast; openImport?: boolean }) {
  const { data, error, loading, reload } = useAsync(
    () => api.suppliers({ includeArchived: true }),
    [],
    'suppliers:all',
  );
  const cats = useAsync(() => api.categories(), [], 'categories');
  const scores = useAsync(() => api.scorecard(), [], 'scorecard');
  const [filter, setFilter] = useState<string>(ALL);
  const [dialog, setDialog] = useState<{ supplier?: Supplier } | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [importing, setImporting] = useState(!!openImport);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [q, setQ] = useState('');
  /** Rândul deschis, cu toate persoanele și istoricul. */
  const [open, setOpen] = useState<string | null>(null);

  const all = data ?? [];
  const scoreOf = useMemo(() => new Map((scores.data ?? []).map((x) => [x.supplier.id, x])), [scores.data]);
  const needle = plain(q.trim());
  const inFilter = (s: Supplier) =>
    (filter === ALL || s.categories.includes(filter)) &&
    (!needle ||
      plain([s.name, ...s.categories, ...s.contacts.flatMap((c) => [c.name, c.email])].join(' ')).includes(
        needle,
      ));
  const active = all.filter((s) => !s.archivedAt);
  const list = active.filter(inFilter);
  const archivedList = all.filter((s) => s.archivedAt).filter(inFilter);

  // Categoriile firmei; pentru o agendă goală, sugestiile implicite.
  const used = cats.data?.used ?? [];
  const suggestions = used.length ? used : (cats.data?.suggested ?? []);
  const countIn = (c: string) => active.filter((s) => s.categories.includes(c)).length;

  const afterChange = () => {
    reload();
    cats.reload();
    scores.reload();
  };

  const remove = async (s: Supplier) => {
    const ok = await ask(
      s.sent > 0
        ? {
            title: t('Arhivezi „{0}”?', s.name),
            message: t(
              'Apare în {0} {1}, deci nu poate fi șters fără să strici istoricul. Arhivat, nu mai apare la cereri noi, dar rămâne în cererile trecute.',
              s.sent,
              s.sent === 1 ? t('cerere') : t('cereri'),
            ),
            confirm: t('Arhivează'),
          }
        : {
            title: t('Ștergi „{0}”?', s.name),
            message: t('Nu apare în nicio cerere, deci nu se pierde nimic.'),
            confirm: t('Șterge furnizorul'),
            danger: true,
          },
    );
    if (!ok) return;

    setBusyId(s.id);
    try {
      const result = await api.removeSupplier(s.id);
      afterChange();
      toast(result.action === 'deleted' ? t('Furnizor șters') : t('Furnizor arhivat'));
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusyId(null);
    }
  };

  const restore = async (s: Supplier) => {
    setBusyId(s.id);
    try {
      await api.restoreSupplier(s.id);
      afterChange();
      toast(t('Furnizor readus în agendă'));
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <div className="head">
        <div>
          <h1>{t('Furnizori')}</h1>
          <p>
            {t(
              'Agenda ta de furnizori. Un distribuitor poate fi trecut pe mai multe categorii și poate avea câte o persoană pe fiecare gamă.',
            )}
          </p>
        </div>
        <div className="acts">
          <button className="btn" onClick={() => setImporting(true)}>
            {t('Importă din Excel')}
          </button>
          <button className="btn primary" onClick={() => setDialog({})}>
            <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
              <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
            {t('Adaugă furnizor')}
          </button>
        </div>
      </div>

      <div className="filters rq-tabs" role="group" aria-label={t('Categoria')}>
        <button type="button" aria-pressed={filter === ALL} onClick={() => setFilter(ALL)}>
          {ALL}
          <span className="n">{active.length}</span>
        </button>
        {suggestions.map((c) => {
          const n = countIn(c);
          return (
            <button
              key={c}
              type="button"
              aria-pressed={c === filter}
              className={n === 0 && c !== filter ? 'zero' : undefined}
              onClick={() => setFilter(c)}
            >
              {c}
              <span className="n">{n}</span>
            </button>
          );
        })}
      </div>

      <section className="sheet rq-panel">
        <div className="rq-tools">
          <label className="rq-search">
            <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden="true">
              <circle cx="7" cy="7" r="4.6" fill="none" stroke="currentColor" strokeWidth="1.5" />
              <path d="m10.5 10.5 3.2 3.2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <input
              type="search"
              value={q}
              placeholder={t('Caută după firmă, persoană sau email…')}
              aria-label={t('Caută un furnizor')}
              onChange={(e) => setQ(e.target.value)}
            />
          </label>
          <span className="rq-count">
            {list.length === 1 ? t('un furnizor') : t('{0} furnizori', list.length)}
          </span>
        </div>

        {loading ? <Loading /> : null}
        {!loading && error ? <ErrorBox error={error} onRetry={reload} /> : null}
        {!loading && !error ? (
          list.length ? (
            <table className="rq-table sp-table">
              <thead>
                <tr>
                  <th>{t('Firmă')}</th>
                  <th>{t('Categorii')}</th>
                  <th>{t('Persoană de contact')}</th>
                  <th>{t('Răspunde la cereri')}</th>
                  <th>{t('Ales')}</th>
                  <th aria-label={t('Acțiuni')} />
                </tr>
              </thead>
              <tbody>
                {list.map((s) => {
                  const main = primaryContact(s);
                  const others = s.contacts.length - 1;
                  const sc = scoreOf.get(s.id);
                  const isOpen = open === s.id;
                  const badge = anafBadge(s.anaf);
                  return (
                    <Fragment key={s.id}>
                      <tr
                        className={isOpen ? 'open' : undefined}
                        onClick={() => setOpen(isOpen ? null : s.id)}
                      >
                        <td className="sp-who">
                          <div className="rq-what-in">
                            <Avatar name={s.name} size="lg" />
                            <span>
                              <button
                                type="button"
                                className="rq-title"
                                aria-expanded={isOpen}
                                onClick={(e) => (e.stopPropagation(), setOpen(isOpen ? null : s.id))}
                              >
                                {s.name}
                              </button>
                              <span className="rq-sub">{main?.email || '—'}</span>
                              {badge ? (
                                <span
                                  className="pill late sp-badge"
                                  title={anafAlerts(s.anaf)
                                    .filter((a) => a.level === 'danger')
                                    .map((a) => a.text)
                                    .join(' ')}
                                >
                                  {badge}
                                </span>
                              ) : null}
                            </span>
                          </div>
                        </td>
                        <td className="sp-cats">
                          <span className="cats">
                            {s.categories.map((c) => (
                              <span className="pill cat" key={c}>
                                {c}
                              </span>
                            ))}
                          </span>
                        </td>
                        <td className="sp-contact">
                          <span className="sp-c-name">
                            {main?.name || <span className="muted">—</span>}
                            {others > 0 ? <span className="sp-more">+{others}</span> : null}
                          </span>
                          {main?.phone ? <span className="rq-sub">{main.phone}</span> : null}
                        </td>
                        <td className="sp-resp">
                          <Response s={s} sc={sc} />
                        </td>
                        <td className="sp-chosen">
                          {sc?.chosen ? (
                            <span className="pill act">
                              {sc.chosen === 1 ? t('o dată') : t('de {0} ori', sc.chosen)}
                            </span>
                          ) : (
                            <span className="muted">—</span>
                          )}
                        </td>
                        <td className="sp-acts" onClick={(e) => e.stopPropagation()}>
                          <MoreMenu
                            busy={busyId === s.id}
                            items={[
                              { label: t('Editează'), run: () => setDialog({ supplier: s }) },
                              {
                                label: s.sent ? t('Arhivează') : t('Șterge furnizorul'),
                                run: () => void remove(s),
                                danger: !s.sent,
                              },
                            ]}
                          />
                        </td>
                      </tr>
                      {isOpen ? (
                        <tr className="sp-detail">
                          <td colSpan={6}>
                            <Detail s={s} sc={sc} onEdit={() => setDialog({ supplier: s })} />
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div className="rq-empty">
              <p>
                {!active.length
                  ? t('Agenda e goală. Adaugă primul furnizor sau importă-i dintr-un tabel Excel.')
                  : needle
                    ? t('Niciun furnizor nu se potrivește.')
                    : t('Niciun furnizor în această categorie.')}
              </p>
              {needle ? (
                <button className="btn small" onClick={() => setQ('')}>
                  {t('Șterge căutarea')}
                </button>
              ) : null}
            </div>
          )
        ) : null}
      </section>

      {archivedList.length ? (
        <section className="sheet rq-panel sp-archived">
          <button
            type="button"
            className="sp-arch-h"
            aria-expanded={showArchived}
            onClick={() => setShowArchived((v) => !v)}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
              <path
                d="m4 2.5 3.5 3.5L4 9.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {t('Furnizori arhivați')}
            <span className="n">{archivedList.length}</span>
            <span className="sp-arch-hint">{t('nu apar la cereri noi, dar rămân în cererile trecute')}</span>
          </button>
          {showArchived ? (
            <table className="rq-table sp-table">
              <tbody>
                {archivedList.map((s) => (
                  <tr key={s.id} className="off static">
                    <td className="sp-who">
                      <div className="rq-what-in">
                        <Avatar name={s.name} size="lg" />
                        <span>
                          <span className="rq-title">{s.name}</span>
                          <span className="rq-sub">{primaryContact(s)?.email || '—'}</span>
                        </span>
                      </div>
                    </td>
                    <td className="sp-cats">{s.categories.join(', ')}</td>
                    <td className="sp-resp">
                      {s.sent ? (
                        t('{0} din {1} oferte', s.answered, s.sent)
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td className="rq-stage">
                      <button
                        className="btn small"
                        disabled={busyId === s.id}
                        onClick={() => void restore(s)}
                      >
                        {t('Readu în agendă')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </section>
      ) : null}

      {importing ? (
        <ImportSuppliersDialog
          existing={all}
          suggestions={suggestions}
          toast={toast}
          onDone={afterChange}
          onClose={() => setImporting(false)}
        />
      ) : null}

      {dialog ? (
        <SupplierDialog
          supplier={dialog.supplier}
          suggestions={suggestions}
          defaultCategory={filter === ALL ? undefined : filter}
          toast={toast}
          onSaved={afterChange}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </>
  );
}

/** Cât răspunde: bara ofertelor din cereri și, dedesubt, în cât timp. */
function Response({ s, sc }: { s: Supplier; sc?: SupplierScore }) {
  const sent = sc?.sent ?? s.sent;
  const answered = sc?.answered ?? s.answered;
  if (!sent) return <span className="muted">{t('nicio cerere încă')}</span>;
  const pct = Math.round((answered / sent) * 100);
  return (
    <span className="sp-rate">
      <span className="sp-rate-top">
        <span className={`sp-bar${pct >= 50 ? '' : ' low'}`} aria-hidden="true">
          <i style={{ width: `${pct}%` }} />
        </span>
        <b>{t('{0} din {1}', answered, sent)}</b>
      </span>
      <span className="rq-sub">
        {sc?.avgDays == null
          ? answered
            ? ''
            : t('n-a răspuns încă')
          : t('în {0} {1}, în medie', fmtNum(sc.avgDays), sc.avgDays === 1 ? t('zi') : t('zile'))}
      </span>
    </span>
  );
}

/** Rândul deschis: toate persoanele, unde livrează și istoricul cu tine. */
function Detail({ s, sc, onEdit }: { s: Supplier; sc?: SupplierScore; onEdit: () => void }) {
  const alerts = anafAlerts(s.anaf).filter((a) => a.level === 'danger');
  return (
    <div className="sp-more-box">
      <div className="sp-col">
        <h3>{t('Persoane de contact')}</h3>
        <ul className="sp-people">
          {s.contacts.map((c) => (
            <li key={c.id}>
              <Avatar name={c.name || c.email} />
              <span>
                <b>
                  {c.name || c.email}
                  {c.isPrimary && s.contacts.length > 1 ? <em>{t('principală')}</em> : null}
                </b>
                <span className="rq-sub">{[c.role, c.email, c.phone].filter(Boolean).join(' · ')}</span>
              </span>
            </li>
          ))}
        </ul>
      </div>
      <div className="sp-col">
        <h3>{t('Istoric cu tine')}</h3>
        <dl className="sp-facts">
          <div>
            <dt>{t('Cel mai bun preț')}</dt>
            <dd>
              {!sc || sc.compared === 0 ? (
                <span className="muted">{t('fără comparație')}</span>
              ) : sc.wins ? (
                <span className="pill ok">{t('{0} din {1}', sc.wins, sc.compared)}</span>
              ) : (
                t('0 din {0}', sc.compared)
              )}
            </dd>
          </div>
          <div>
            <dt>{t('Ultima dată')}</dt>
            <dd>{sc?.lastAt ? daysAgo(sc.lastAt) : <span className="muted">—</span>}</dd>
          </div>
          <div>
            <dt>{t('Livrează în')}</dt>
            <dd>
              {s.counties.length ? countiesText(s.counties) : <span className="muted">{t('nespus')}</span>}
            </dd>
          </div>
          {s.cui ? (
            <div>
              <dt>{legal().taxId}</dt>
              <dd>{s.cui}</dd>
            </div>
          ) : null}
        </dl>
        {alerts.length ? <p className="sp-alert">{alerts.map((a) => a.text).join(' ')}</p> : null}
        <button className="btn small" onClick={onEdit}>
          {t('Editează furnizorul')}
        </button>
      </div>
    </div>
  );
}
